import type {
  MatchPushPlan,
  MatchPushSampleBody,
  PlanMoment,
  PlanStatus,
  PushLanguage,
  TemplateMoment,
} from '@/types/matchPushes';
import { istClock, istDayKey } from '@/utils/cricketCalendar';

/** Variables a match push title or body may use (spec section 8). */
export const COPY_VARIABLES = [
  { name: 'us', help: "the player's country's team" },
  { name: 'them', help: 'the opponent' },
  { name: 'match_no', help: 'for example 1st T20I' },
  { name: 'series', help: 'the series name' },
  { name: 'city', help: 'the venue city' },
  { name: 'time', help: 'IST start, HH:MM' },
] as const;

export const TITLE_MAX = 100;
export const BODY_MAX = 300;

/** Preview font: Noto Sans Devanagari where the machine has it, else the system font (no web font is loaded). */
export const PUSH_FONT = '"Noto Sans Devanagari", system-ui';

export const LANGUAGES: PushLanguage[] = ['en', 'hi', 'hinglish'];
export const LANGUAGE_LABEL: Record<PushLanguage, string> = {
  en: 'English',
  hinglish: 'Hinglish',
  hi: 'Hindi',
};
export const LANGUAGE_SHORT: Record<PushLanguage, string> = {
  en: 'EN',
  hinglish: 'Hinglish',
  hi: 'HI',
};

export const PLAN_MOMENTS: PlanMoment[] = [
  'night_before',
  'one_hour',
  'toss',
  'innings_break',
  'result',
];
export const MOMENT_LABEL: Record<PlanMoment, string> = {
  night_before: 'Night before',
  one_hour: '1 hour to go',
  toss: 'Toss done',
  innings_break: 'Innings break',
  result: 'Result',
};
export const TEMPLATE_MOMENTS: TemplateMoment[] = [
  'night_before',
  'one_hour',
  'toss',
  'innings_break',
  'result_win',
  'result_loss',
  'result_other',
];
export const TEMPLATE_MOMENT_LABEL: Record<TemplateMoment, string> = {
  night_before: 'Night before',
  one_hour: '1 hour to go',
  toss: 'Toss done',
  innings_break: 'Innings break',
  result_win: 'Result: win',
  result_loss: 'Result: loss',
  result_other: 'Result: other',
};

export const STATUS_LABEL: Record<PlanStatus, string> = {
  scheduled: 'Scheduled',
  waiting: 'Waiting',
  due: 'Due',
  sending: 'Sending',
  sent: 'Sent',
  skipped: 'Skipped',
  paused: 'Paused',
  cancelled: 'Cancelled',
};
/** Ant Design tag colours, matching the mock-up (sent green, scheduled blue, waiting amber, skipped grey). */
export const STATUS_COLOR: Record<PlanStatus, string> = {
  scheduled: 'blue',
  waiting: 'orange',
  due: 'gold',
  sending: 'processing',
  sent: 'green',
  skipped: 'default',
  paused: 'purple',
  cancelled: 'default',
};

/** Sample match for the copy preview. */
export const SAMPLE_MATCH: Record<PushLanguage, Record<string, string>> = {
  en: {
    us: 'India',
    them: 'New Zealand',
    match_no: '1st T20I',
    series: 'India tour of New Zealand',
    city: 'Christchurch',
    time: '12:30',
  },
  hinglish: {
    us: 'India',
    them: 'New Zealand',
    match_no: '1st T20I',
    series: 'India tour of New Zealand',
    city: 'Christchurch',
    time: '12:30',
  },
  // Team names render in Devanagari for Hindi (spec section 8).
  hi: {
    us: 'भारत',
    them: 'न्यूज़ीलैंड',
    match_no: '1st T20I',
    series: 'India tour of New Zealand',
    city: 'Christchurch',
    time: '12:30',
  },
};

const TOKEN = /\{([^{}]*)\}/g;

/**
 * Fills `{var}` tokens from `vars`. Unknown tokens are removed and the double
 * spaces they leave are collapsed (spec 8.1), then the ends are trimmed. The
 * backend renders the real sends and validates on save; this only previews.
 */
export function renderTemplate(text: string, vars: Record<string, string>): string {
  return text
    .replace(TOKEN, (_, name: string) =>
      Object.prototype.hasOwnProperty.call(vars, name) ? vars[name] : ''
    )
    .replace(/ {2,}/g, ' ')
    .trim();
}

/** Token names in `text` that are not one of the six variables (without braces, no duplicates). */
export function unknownTokens(text: string): string[] {
  const known = new Set<string>(COPY_VARIABLES.map((v) => v.name));
  const out = new Set<string>();
  for (const match of text.matchAll(TOKEN)) {
    if (!known.has(match[1])) out.add(match[1]);
  }
  return [...out];
}

/** Emoji allowed in copy: the older set that Android 9 to 11 draw. */
export const ALLOWED_EMOJI = ['🏏', '🔥', '🏆', '⏳', '☕', '💪', '🎉'];

/** Emoji in `text` outside the allowed set. */
export function disallowedEmoji(text: string): string[] {
  const out = new Set<string>();
  for (const match of text.matchAll(/\p{Extended_Pictographic}/gu)) {
    if (!ALLOWED_EMOJI.includes(match[0])) out.add(match[0]);
  }
  return [...out];
}

/** Reasons a title and body would be refused on save (the backend still decides); empty when fine. */
export function copyProblems(title: string, body: string, language: PushLanguage): string[] {
  const vars = SAMPLE_MATCH[language];
  const problems: string[] = [];
  const unknown = [...new Set([...unknownTokens(title), ...unknownTokens(body)])];
  if (unknown.length) {
    const names = unknown.map((u) => `{${u}}`).join(', ');
    problems.push(`Unknown variable${unknown.length > 1 ? 's' : ''}: ${names}`);
  }
  if (title.length > TITLE_MAX) problems.push(`Title is over ${TITLE_MAX} characters`);
  if (body.length > BODY_MAX) problems.push(`Body is over ${BODY_MAX} characters`);
  if (!renderTemplate(title, vars) || !renderTemplate(body, vars)) {
    problems.push('Title and body cannot be empty');
  }
  return problems;
}

/** Soft warnings: emoji outside the old set may not draw on Android 9 to 11. */
export function copyWarnings(title: string, body: string): string[] {
  const emoji = [...new Set([...disallowedEmoji(title), ...disallowedEmoji(body)])];
  return emoji.length
    ? [`${emoji.join(' ')} may not show on older Android; use ${ALLOWED_EMOJI.join(' ')}`]
    : [];
}

/**
 * Languages a plan's audience can get. Only Hindi-family players (country IN)
 * are language-tested or switched; everyone else gets English.
 */
export function planLanguages(audienceCountries: string[], mode: string): PushLanguage[] {
  if (!audienceCountries.includes('IN')) return ['en'];
  if (mode === 'experiment') return ['en', 'hi', 'hinglish'];
  const picked = (['en', 'hi', 'hinglish'] as PushLanguage[]).includes(mode as PushLanguage)
    ? (mode as PushLanguage)
    : 'en';
  return picked === 'en' ? ['en'] : ['en', picked];
}

/** Rough minutes after the start each live moment tends to happen (T20 shape); only shown after "~". */
const EXPECTED_OFFSET_MIN: Partial<Record<PlanMoment, number>> = {
  toss: -30,
  innings_break: 90,
  result: 195,
};

/**
 * The time cell of a plan: the send time for a fixed plan ('Wed 20:00' when it
 * is not on the match day, else 'HH:MM'), the time the moment was seen for a
 * live plan, or '~HH:MM' when it is still an estimate.
 */
export function planTimeLabel(plan: MatchPushPlan): string {
  const fixtureDay = istDayKey(new Date(plan.fixture.startsAt).getTime());
  const withDay = (iso: string): string => {
    const day = istDayKey(new Date(iso).getTime());
    if (day === fixtureDay) return istClock(iso);
    const weekday = new Date(`${day}T00:00:00Z`).toUTCString().slice(0, 3);
    return `${weekday} ${istClock(iso)}`;
  };
  if (plan.kind === 'fixed' && plan.scheduledAt) return withDay(plan.scheduledAt);
  if (plan.dueAt) return withDay(plan.dueAt);
  const offset = EXPECTED_OFFSET_MIN[plan.moment];
  if (offset === undefined) return '—';
  const at = new Date(new Date(plan.fixture.startsAt).getTime() + offset * 60_000).toISOString();
  return `~${withDay(at)}`;
}

/** The test-send body: `language` only when one is picked (none = each player's own language). */
export function sampleBody(userIds: string[], language?: PushLanguage): MatchPushSampleBody {
  return language ? { userIds, language } : { userIds };
}

/** Statuses an admin may still change (pause, cancel, edit). */
export const OPEN_STATUSES: PlanStatus[] = ['scheduled', 'waiting', 'due', 'paused'];

/** How a plan's custom copy can be edited. Result copy depends on win or loss, so it takes no per-plan text. */
export type CustomCopyMode = 'editable' | 'clear-only' | 'none';

export function customCopyMode(
  plan: Pick<MatchPushPlan, 'moment' | 'copyOverride'>
): CustomCopyMode {
  if (plan.moment !== 'result') return 'editable';
  return plan.copyOverride ? 'clear-only' : 'none';
}

export const RESULT_COPY_NOTE =
  "Result copy depends on whether the player's team won or lost. Edit the result_win / result_loss / result_other templates in the Copy tab.";

/** Players a plan reached: below 100 the exact number, from 100 'about N' rounded to the hundred. */
export function audienceCount(estimated: number | null, sent: boolean): string {
  if (estimated === null) return '—';
  const n = (v: number) => v.toLocaleString('en-IN');
  if (sent) return `${n(estimated)} players`;
  return estimated < 100 ? `${n(estimated)}` : `about ${n(Math.round(estimated / 100) * 100)}`;
}

/**
 * Delivered count from a plan's stats when it is there: a number, or an object
 * of numbers (per country, language or version) which is summed. Null otherwise.
 */
export function deliveredCount(stats: Record<string, unknown> | null): number | null {
  const sum = (value: unknown): number | null => {
    if (typeof value === 'number') return value;
    if (typeof value === 'object' && value !== null) {
      let total = 0;
      let found = false;
      for (const inner of Object.values(value)) {
        const part = sum(inner);
        if (part !== null) {
          total += part;
          found = true;
        }
      }
      return found ? total : null;
    }
    return null;
  };
  return stats ? sum(stats.delivered) : null;
}
